import React, { useState, useEffect, useCallback } from 'react';
import { iconBtn, iconBtnDanger } from '../../components/ui/iconButton';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import Table from '../../components/Table';
import PageHeader from '../../components/PageHeader';
import Btn from '../../components/ui/Btn';
import StatusTag from '../../components/ui/StatusTag';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { toastSuccess } from '../../utils/toast';
import { proyectService } from '../../services/proyectService';
import { useAuth } from '../../context/AuthContext';
import { formatdateWithTime } from '../../utils/helpers';


const Proyects = () => {
  const navigate = useNavigate();
  const { role } = useAuth();
  const isEmployee = role === 'employee';   // un empleado ve solo su proyecto interno y no lo edita aquí
  const [proyects, setProyects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toDelete, setToDelete] = useState(null);

  const fetchProyects = useCallback(async () => {
    try { setProyects(await proyectService.getAll()); }
    catch (error) { console.error('Error al obtener proyectos:', error.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchProyects(); }, [fetchProyects]);

  const handleEdit = (proyect) => navigate('/proyects/edit', { state: { isEdit: true, proyectEdit: proyect } });

  const allColumns = [
    { key: 'id', title: 'ID', sortable: true, render: (v) => <span className="font-mono tabular-nums text-fg-dim">{v}</span> },
    {
      key: 'name', title: 'Proyecto', sortable: true,
      render: (value, p) => (
        <div className="min-w-[180px]">
          <p className="font-semibold text-fg">{value}</p>
          {p.description && <p className="text-[12.5px] text-fg-faint line-clamp-1 max-w-[42ch]">{p.description}</p>}
        </div>
      ),
    },
    { key: 'project_types', title: 'Tipo', sortable: true, render: (v) => v?.name || '—' },
    { key: 'project_manager', title: 'Gerente', sortable: true, render: (v) => v?.name || '—' },
    { key: 'platform', title: 'Plataforma', sortable: true, render: (v) => (v ? v.charAt(0).toUpperCase() + v.slice(1) : '—') },
    {
      key: 'status', title: 'Estado', sortable: true,
      render: (v) => (v === 'active' ? <StatusTag tone="live" dot>Activo</StatusTag> : <StatusTag>{v === 'inactive' ? 'Inactivo' : v || '—'}</StatusTag>),
    },
    { key: 'created_at', title: 'Creado', sortable: true, render: (v) => <span className="whitespace-nowrap tabular-nums">{formatdateWithTime(v)}</span> },
    {
      key: 'actions', title: <span className="sr-only">Acciones</span>, sortable: false,
      hidden: isEmployee,
      render: (_, p) => (
        <div className="flex justify-end gap-1">
          <button type="button" disabled={p.status === 'inactive'} onClick={() => handleEdit(p)} className={iconBtn} aria-label={`Editar ${p.name}`} title="Editar"><FaEdit aria-hidden /></button>
          <button type="button" disabled={p.status === 'inactive'} onClick={() => setToDelete(p)} className={iconBtnDanger} aria-label={`Eliminar ${p.name}`} title="Eliminar"><FaTrash aria-hidden /></button>
        </div>
      ),
    },
  ];

  const columns = allColumns.filter((c) => !c.hidden);

  return (
    <div className="max-w-6xl mx-auto">
      <PageHeader title="Proyectos" subtitle={isEmployee ? 'Tu proyecto interno: aquí viven tus agentes y clones hasta que un administrador los pase al proyecto de un cliente.' : 'Cada cliente tiene su proyecto: sus módulos, permisos y agentes. Desde Agentes también puedes crear uno en el mismo paso.'}>
        {!isEmployee && <Btn variant="primary" size="lg" icon={FaPlus} onClick={() => navigate('/proyects/add', { state: { isEdit: false, proyectEdit: null } })}>Nuevo proyecto</Btn>}
      </PageHeader>
      <Table data={proyects} columns={columns} loading={loading}
        options={{ pagination: true, pageSize: 10, pageSizeOptions: [10, 20, 50], showSearch: true, searchPlaceholder: 'Buscar proyectos', emptyMessage: 'Todavía no hay proyectos.' }} />
      <ConfirmModal open={Boolean(toDelete)} onClose={() => setToDelete(null)} title={`Eliminar «${toDelete?.name ?? ''}»`} confirmLabel="Eliminar proyecto" tone="danger"
        onConfirm={async () => { await proyectService.deleteProyect(toDelete.id); await fetchProyects(); toastSuccess('Proyecto eliminado'); }}>
        <p>Se elimina el proyecto con su configuración. Esta acción no se puede deshacer.</p>
      </ConfirmModal>
    </div>
  );
};

export default Proyects;
